const { Matrix } = require('./qrcode');
const { SquareQRCodeEncoder } = require('./square-qrcode');

const JEWEL_VALUE = Object.freeze({
  AVAILABLE: 'AVAILABLE',
  FALSE: 'FALSE',
  TRUE: 'TRUE',
  UNDECIDED: 'UNDECIDED',
});

/**
 * CLASS MarketQRCodeEncoder
 * This is used to generate an SVG for a QR code with a Square logo embedded in the center.
 * Contact #qrcode-design for design specifics
 * The following options can be specified:
 *   border          boolean (default false) whether to add a 6-pixel quiet zone
 *   invert          boolean (default false) light data on dark background when on
 *   monochrome      boolean (default false) whether to make it all black and white
 *                   This is useful for things like thermal printers that only print black
 *   styleBackground string to override style inserted in SVG background rectangle
 *   styleForeground string to override style inserted in SVG foreground data and patterns
 *   styleLogo       string to override style inserted in SVG for Square jewel
 */
class MarketQRCodeEncoder extends SquareQRCodeEncoder {
  static getJewel() {
    if (this.jewel !== undefined) return this.jewel;
    const jewel = Matrix.withAll(9, 9, JEWEL_VALUE.UNDECIDED);
    // corners are available
    jewel.put(0, 0, JEWEL_VALUE.AVAILABLE);
    jewel.put(0, 8, JEWEL_VALUE.AVAILABLE);
    jewel.put(8, 0, JEWEL_VALUE.AVAILABLE);
    jewel.put(8, 8, JEWEL_VALUE.AVAILABLE);
    // forced false
    for (let offset = 0; offset < 7; offset++) {
      jewel.put(offset + 1, 0, JEWEL_VALUE.FALSE);
      jewel.put(offset + 1, 8, JEWEL_VALUE.FALSE);
      jewel.put(0, offset + 1, JEWEL_VALUE.FALSE);
      jewel.put(8, offset + 1, JEWEL_VALUE.FALSE);
    }
    jewel.put(3, 3, JEWEL_VALUE.FALSE);
    jewel.put(3, 5, JEWEL_VALUE.FALSE);
    jewel.put(5, 3, JEWEL_VALUE.FALSE);
    jewel.put(5, 5, JEWEL_VALUE.FALSE);
    // forced true
    for (let offset = 0; offset < 6; offset++) {
      jewel.put(1 + offset, 1, JEWEL_VALUE.TRUE);
      jewel.put(2 + offset, 7, JEWEL_VALUE.TRUE);
      jewel.put(7, 1 + offset, JEWEL_VALUE.TRUE);
      jewel.put(1, 2 + offset, JEWEL_VALUE.TRUE);
    }
    jewel.put(4, 4, JEWEL_VALUE.TRUE);
    this.jewel = jewel;
    return jewel;
  }

  initializeSvg() {
    // generate the SVG
    const offset = this.svgOffset;
    const dimension = this.svgDimension;
    const foregroundStyle = this.svgForegroundStyle;
    const logoStyle = this.svgLogoStyle;
    const source = [];
    source.push(this.svgTag(dimension));
    source.push('<defs>');
    source.push(this.svgFinderPattern(foregroundStyle));
    source.push(this.svgLogo(logoStyle));
    source.push('</defs>');
    source.push(this.svgBackground(this.svgBackgroundStyle, dimension, offset === 0 ? undefined : 16));
    this.svgFinderPatterns(source, offset);
    this.svgTurtle(source, foregroundStyle, offset);
    // Square logo
    const logoPosition = (this.code.dimension - 7) * 4 + offset;
    source.push(`<use xlink:href="#s" x="${logoPosition}" y="${logoPosition}" />`);
    // finish
    source.push('</svg>');
    this.svg = source.join('');
  }

  initializeTurtleData() {
    super.initializeTurtleData();
    // clear out middle for Square logo
    const start = (this.code.dimension - 9) / 2;
    this.turtleData.fill(start, start + 1, 9, 7, false);
    this.turtleData.fill(start + 1, start, 7, 1, false);
    this.turtleData.fill(start + 1, start + 8, 7, 1, false);
  }

  get isMonochrome() {
    const isMonochrome = this.code.options.monochrome;
    if (isMonochrome === undefined) return false;
    return isMonochrome;
  }

  get minimumVersion() {
    // if the code version is 1, then the jewel would cut out the timing patterns
    return 2;
  }

  penaltyForMatrix(matrix) {
    const errorCorrectionCapacity = this.map.errorCorrectionCapacity;
    const messedUpBytes = errorCorrectionCapacity.map(() => new Set());
    // figure out the messed up bytes based on putting the jewel in the center
    const jewel = this.constructor.getJewel();
    const start = (matrix.content.length - 9) / 2;
    for (let y = 0; y < 9; y++) {
      const yData = y + start;
      for (let x = 0; x < 9; x++) {
        const xData = x + start;
        const pixel = this.map.at(xData, yData);
        if (this.map.isDataPixel(pixel)) {
          switch (jewel.at(x, y)) {
            case JEWEL_VALUE.FALSE:
              if (matrix.at(xData, yData) !== false) messedUpBytes[pixel.chunk].add(pixel.byte);
              break;
            case JEWEL_VALUE.TRUE:
              if (matrix.at(xData, yData) !== true) messedUpBytes[pixel.chunk].add(pixel.byte);
              break;
            case JEWEL_VALUE.UNDECIDED:
              messedUpBytes[pixel.chunk].add(pixel.byte);
              break;
            default:
          }
        }
      }
    }
    // calculate penalty based on how many messed up bytes
    let penalty = 0;
    errorCorrectionCapacity.forEach((capacity, index) => {
      const leftoverCapacity = capacity - messedUpBytes[index].size * 2;
      if (leftoverCapacity < 0) {
        penalty += this.constructor.PENALTY_FOR_NEGATIVE_CAPACITY;
      } else if (leftoverCapacity < this.constructor.PENALTY_FOR_LEFTOVER_CAPACITY.length) {
        penalty += this.constructor.PENALTY_FOR_LEFTOVER_CAPACITY[leftoverCapacity];
      }
    });
    // add additional penalties on trying to have the corners be filled
    if (!matrix.at(start, start)) penalty++;
    if (!matrix.at(start, start + 8)) penalty++;
    if (!matrix.at(start + 8, start)) penalty++;
    if (!matrix.at(start + 8, start + 8)) penalty++;
    return penalty;
  }

  get svgForegroundStyle() {
    const fromOptions = this.code.options.styleForeground;
    if (fromOptions !== undefined) return fromOptions;
    if (this.inverted) {
      return this.isMonochrome ? 'fill="white"' : 'fill="white" fill-opacity="0.95"';
    } else {
      return this.isMonochrome ? 'fill="black"' : 'fill="black" fill-opacity="0.9"';
    }
  }

  get svgLogoStyle() {
    if (this.isMonochrome) return this.svgForegroundStyle;
    const fromOptions = this.code.options.styleLogo;
    if (fromOptions !== undefined) return fromOptions;
    return 'fill="#006AFF"';
  }

  svgLogo(style) {
    return `<path id="s" ${style} fill-rule="evenodd" d="M 0 9.8 C 0 3.92 3.92 0 9.8 0 L 46.2 0 C 52.08 0 56 3.92 56 9.8 L 56 46.2 C 56 52.08 52.08 56 46.2 56 L 9.8 56 C 3.92 56 0 52.08 0 46.2 Z M 11.2 14 C 11.2 12.32 12.32 11.2 14 11.2 L 42 11.2 C 43.68 11.2 44.8 12.32 44.8 14 L 44.8 42 C 44.8 43.68 43.68 44.8 42 44.8 L 14 44.8 C 12.32 44.8 11.2 43.68 11.2 42 Z M 21 22.4 C 21 21.7 21.7 21 22.4 21 L 33.6 21 C 34.3 21 35 21.7 35 22.4 L 35 33.6 C 35 34.3 34.3 35 33.6 35 L 22.4 35 C 21.7 35 21 34.3 21 33.6 Z" />`;
  }

  get svgOffset() {
    return this.hasBorder ? 48 : 0;
  }
}

module.exports = { MarketQRCodeEncoder, JEWEL_VALUE };
